/*
 * Which emails have already produced a ticket.
 *
 * Filing a batch of mail, it is easy to open the same message twice — the pane
 * stays pinned while you move between messages, which is exactly the workflow
 * this add-in is for. A duplicate ticket is cheap to create and annoying to
 * clean up, so the pane says something before it happens.
 *
 * This is the one place roamingSettings is the right store, and deliberately
 * the opposite call from lib/session.ts. Microsoft documents roamingSettings as
 * insecure — it is readable through Microsoft Graph — which rules it out for
 * the session token. A map of message ids to ticket numbers is not a secret,
 * and roaming is exactly the behaviour wanted here: the same person filing from
 * a laptop and from the web should not be warned twice, or not at all.
 */

/* global Office */

export interface RecentTicket {
  number: number;
  /** RFC 3339. Only used to decide which entries to drop. */
  createdAt: string;
}

export type RecentTickets = Record<string, RecentTicket>;

const STORAGE_KEY = "valoon.recentTickets";

/**
 * How many emails to remember. Enough to cover a filing session and a few days
 * of coming back to the same thread, and small enough that the map stays far
 * under the tens of kilobytes Exchange allows an add-in's settings.
 */
export const MAX_ENTRIES = 50;

/**
 * Validates whatever came back out of roamingSettings.
 *
 * Nothing guarantees the stored value is still the shape this version writes —
 * an older build, a half-finished save, or another add-in version could have
 * left anything there. Entries that do not parse are dropped rather than
 * repaired.
 */
export function parseRecent(raw: unknown): RecentTickets {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }
  const out: RecentTickets = {};
  for (const [messageId, value] of Object.entries(raw as Record<string, unknown>)) {
    const entry = value as Partial<RecentTicket> | null;
    if (!entry || typeof entry.number !== "number" || !Number.isFinite(entry.number)) {
      continue;
    }
    out[messageId] = {
      number: entry.number,
      createdAt: typeof entry.createdAt === "string" ? entry.createdAt : "",
    };
  }
  return out;
}

/**
 * Adds an entry, keeping only the newest MAX_ENTRIES.
 *
 * Undatable entries are dropped first: they came from a version that did not
 * record the date, so they are the oldest thing in the map by definition.
 */
export function withTicket(
  entries: RecentTickets,
  messageId: string,
  ticket: RecentTicket,
  max: number = MAX_ENTRIES
): RecentTickets {
  const next: RecentTickets = { ...entries, [messageId]: ticket };

  const ids = Object.keys(next);
  if (ids.length <= max) {
    return next;
  }

  ids.sort((a, b) => order(next[b]) - order(next[a]));
  const pruned: RecentTickets = {};
  for (const id of ids.slice(0, max)) {
    pruned[id] = next[id];
  }
  return pruned;
}

function order(ticket: RecentTicket): number {
  const at = Date.parse(ticket.createdAt);
  return Number.isNaN(at) ? 0 : at;
}

function settings(): Office.RoamingSettings | undefined {
  try {
    return typeof Office !== "undefined" ? Office.context?.roamingSettings : undefined;
  } catch {
    return undefined;
  }
}

/** The ticket already created from this email, if there is one. */
export function recentTicketFor(messageId: string): RecentTicket | null {
  if (!messageId) {
    return null;
  }
  try {
    return parseRecent(settings()?.get(STORAGE_KEY))[messageId] ?? null;
  } catch {
    return null;
  }
}

/**
 * Records that this email produced a ticket.
 *
 * Failures are swallowed on purpose. The ticket exists either way, and the
 * cost of losing this is one missing warning later — not something worth
 * showing an error over a ticket that was created successfully.
 */
export function rememberTicket(messageId: string, number: number, now: Date = new Date()): void {
  const store = settings();
  if (!store || !messageId) {
    return;
  }
  try {
    const next = withTicket(parseRecent(store.get(STORAGE_KEY)), messageId, {
      number,
      createdAt: now.toISOString(),
    });
    store.set(STORAGE_KEY, next);
    store.saveAsync(() => {
      // Nothing to do either way: see above.
    });
  } catch {
    // Ignore.
  }
}
